// biome-ignore format: historic
// biome-ignore format: historic
// biome-ignore format: historic
import Input from '@components/Input/Input';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import styles from './BulkInput.module.scss';

interface BulkInputProps {
  values: string[] | undefined;
  onChange: (newValues: string[]) => void;
  placeholders?: (index: number) => string;
  enableMultiMode?: boolean;
  locked?: boolean;
}

interface BulkInputHandle {
  nodes: HTMLInputElement[];
}

const BulkInput = ({
  ref: forwardedRef,
  values = [''],
  onChange,
  placeholders,
  enableMultiMode = false,
  locked,
}: BulkInputProps & {
  ref?: React.Ref<BulkInputHandle>;
}) => {
  const [multiMode, setMultiMode] = useState(false);
  const [rawMultiText, setRawMultiText] = useState('');
  const inputsRef = useRef<HTMLInputElement[]>([]);
  const inputsRefCallback = useCallback((node: HTMLInputElement) => {
    if (node !== null) {
      inputsRef.current.push(node);
    }
  }, []) as React.RefCallback<HTMLInputElement>;

  useImperativeHandle(forwardedRef, () => ({
    nodes: inputsRef.current,
  }));

  function handleKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      if (!inputsRef.current?.length) {
        return;
      }

      const lastInput = inputsRef.current[inputsRef.current.length - 1];
      lastInput.focus();
    }
  }

  function onSingleChange(newValue: string, index: number) {
    let newValues;

    if (newValue === '') {
      // If the value is empty, remove it from the array
      const copyOfArray = [...values];
      copyOfArray.splice(index, 1);
      newValues = copyOfArray;
    } else if (index >= values.length) {
      newValues = [...values, newValue];
    } else {
      newValues = [...values].map((value, i) => {
        if (i === index) {
          return newValue;
        }
        return value;
      });
    }

    onChange(newValues);
  }

  function onMultiChange(value: string) {
    const newValues = value.split(/,?\s+/);
    onChange(newValues);
    setRawMultiText(value);
  }

  function handleSwitchMode() {
    setMultiMode((mode) => !mode);
  }

  useEffect(() => {
    setRawMultiText(values.join(', '));
  }, [values]);

  const switchModeBtnText = multiMode ? 'Switch to single add mode' : 'Switch to bulk add mode';
  const valuesToShow = values.length < 2 && values[0] === '' ? values : [...values, ''];

  return (
    <div className={styles['bulk-input']} data-num-values={values.length}>
      {!multiMode && (
        <div className={styles['bulk-inputs-container']}>
          {valuesToShow.map((value, i) => {
            const isPlaceholder =
              i === valuesToShow.length - 1 && valuesToShow[valuesToShow.length - 2] === '';
            const placeholder = placeholders ? placeholders(i) : '';

            return (
              <div
                className={styles['bulk-input-input-container']}
                key={i}
                data-placeholder={isPlaceholder}
              >
                <Input
                  value={value}
                  onChange={(value) => onSingleChange(value, i)}
                  placeholder={isPlaceholder ? '' : placeholder}
                  disabled={isPlaceholder || locked}
                  ref={inputsRefCallback}
                  contained
                  enableClearButton={!locked}
                  onKeyUp={handleKeyPress}
                />
              </div>
            );
          })}
        </div>
      )}

      {multiMode && (
        <div className={styles['multi-input-container']}>
          <Input value={rawMultiText} onChange={(value) => onMultiChange(value)} multiLine />
        </div>
      )}

      {enableMultiMode && (
        <div className={styles['switch-mode-container']}>
          <button className={styles['switch-mode-btn']} onClick={handleSwitchMode} type="button">
            {switchModeBtnText}
          </button>
        </div>
      )}
    </div>
  );
};

BulkInput.displayName = 'BulkInput';

export default BulkInput;
